import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ListItem from '../ListItem';
import DropDown from '../DropDown';

import { formatItems, filterItems, NoData } from '../Select';
import GlobalActions from './GlobalActions';

function checkItems(formattedItems = [], checkedItems = []) {
  return formattedItems.map(({ caption, value }) => ({
    caption,
    value,
    isChecked: checkedItems.includes(value),
  }));
}

function updateCheckedItems(checkedItems = [], { value, isChecked }) {
  return isChecked ? checkedItems.filter(el => el !== value) : [...checkedItems, value];
}

export default function SelectMulti({
  checkedItems,
  withGlobalActions,
  items,
  noDataPlaceholder,
  noResultsPlaceholder,
  numberOfVisibleItems,
  onSelect,
  placeholder,
  withSearch,
}) {
  const [searchText, setSearchText] = useState('');

  const formattedItems = formatItems(items);
  const itemsToDisplay = filterItems(formattedItems, searchText);
  const checkedFormattedItems = checkItems(itemsToDisplay, checkedItems);

  const displayedValues = checkedFormattedItems
    .filter(({ isChecked }) => isChecked)
    .map(({ caption }) => caption)
    .sort()
    .join(', ');

  function onItemClick(value, isChecked) {
    return () => {
      const updatedCheckedItems = updateCheckedItems(checkedItems, {
        value,
        isChecked,
      });
      onSelect(updatedCheckedItems);
    };
  }

  function selectAll() {
    onSelect(formattedItems.map(({ value }) => value));
  }

  function clearAll() {
    onSelect([]);
  }

  const hasData = items.length > 0;
  const hasResults = itemsToDisplay.length > 0;
  const isSearching = items.length !== itemsToDisplay.length;
  const showGlobalActions = withGlobalActions && hasData && !isSearching;
  const hasNoResults = hasData && !hasResults;

  return (
    <div className="SelectMulti">
      <DropDown
        numberOfVisibleItems={numberOfVisibleItems}
        onSearch={setSearchText}
        placeholder={displayedValues || placeholder}
        withSearch={withSearch && hasData}
        foldOnSelect={false}
      >
        {!hasData && <NoData value={noDataPlaceholder} />}
        {hasNoResults && <NoData value={noResultsPlaceholder} />}

        {showGlobalActions && <GlobalActions clearAll={clearAll} selectAll={selectAll} />}

        {checkedFormattedItems.map(({ value, caption, isChecked }) => {
          return (
            <ListItem
              key={value}
              active={isChecked}
              withCheckbox
              onClick={onItemClick(value, isChecked)}
            >
              <span>{caption}</span>
            </ListItem>
          );
        })}
      </DropDown>
    </div>
  );
}

SelectMulti.propTypes = {
  checkedItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))
    .isRequired,
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.exact({
        value: PropTypes.any,
        caption: PropTypes.string,
      }),
    ]),
  ).isRequired,
  noDataPlaceholder: PropTypes.string,
  noResultsPlaceholder: PropTypes.string,
  numberOfVisibleItems: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  withGlobalActions: PropTypes.bool,
  withSearch: PropTypes.bool,
};

SelectMulti.defaultProps = {
  noDataPlaceholder: 'No data',
  noResultsPlaceholder: 'No results',
  numberOfVisibleItems: 5,
  placeholder: '-- Select items --',
  withGlobalActions: true,
  withSearch: false,
};
